import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/attachmentTemplate';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { attachmentTableTitles, disabledAttachmentTypes } from 'constants.js';
import { resetListsData } from 'store/lists/actions';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';
import { getEnvVar } from 'helpers';

const {
  getPaginatedAttachmentTemplates,
  deleteAttachmentTemplates,
  unsetCurrentAttachmentTemplate,
  setCurrentAttachmentTemplate,
  warnAndArchiveAttachmentTemplate,
  setCopyCurrentAttachmentTemplate,
  getAttachmentTags,
  updateAttachmentFilters,
  resetAttachmentFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const Template = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const action = getPaginatedAttachmentTemplates({ force: true });
  const templates = useSelector(
    store => store.attachmentTemplate.paginatedList
  );
  const filters = useSelector(
    store => store.attachmentTemplate.filters
  );
  const isLoading = useSelector(
    store => store.attachmentTemplate.gettingPaginated
  );
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.productCategories
  );
  const tags = useSelector(store => store.attachmentTemplate.tagList);

  // Filter out disabled attachment types based on env config
  const disabledAttachmentTypesFromEnv = getEnvVar('ADM_DISABLED_ATTACHMENT_TYPES');
  const admDisabledAttachmentTypes = Array.isArray(disabledAttachmentTypesFromEnv)
    ? disabledAttachmentTypesFromEnv
    : typeof disabledAttachmentTypesFromEnv === 'string'
      ? disabledAttachmentTypesFromEnv
          .split(',')
          .map(type => type.trim())
          .filter(Boolean)
      : [...disabledAttachmentTypes];
  const allOptionsToFilterType = [
    { name: 'type', value: 'DOC', checked: false },
    { name: 'type', value: 'PDF', checked: false },
    { name: 'type', value: 'PNG', checked: false }
  ];
  const optionsToFilterType = allOptionsToFilterType.filter(option => !admDisabledAttachmentTypes.includes(option.value));
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'GLOBAL DEFAULT',
      value: 'global',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];
  const optionsToFilterTags = tags
    ? tags.map(tag => {
        return { name: 'tags', value: tag, checked: false };
      })
    : [];
  const optionsToFilterMarketingType = [
    { name: 'marketingType', value: 'Promotional', checked: false },
    { name: 'marketingType', value: 'Transactional', checked: false }
  ];

  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false); 

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedAttachmentTemplates({ force: true }));
    dispatch(getAttachmentTags());
    dispatch(resetListsData());
  }, []);

  const onClickArchive = attachmentTemplate => {
    let action = deleteAttachmentTemplates({
      templateUuids: [attachmentTemplate.uuid],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Attachment', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog
    };
    if (filters?.displayActive) {
      dispatch(
        warnAndArchiveAttachmentTemplate({
          uuid: attachmentTemplate.uuid,
          message: warnMessage
        })
      );
    } else {
      dispatch(action);
    }
  };

  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Attachments`,
      action: () => onClickToggleArchive(action, updateAttachmentFilters, { displayActive: !filters?.displayActive, pageNo: 0 }),
    },
    {
      name: 'Add Attachment',
      action: () => onClickAdd(unsetCurrentAttachmentTemplate(), `/attachment-templates/details`),
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const getActionButtons = attachmentTemplate => [
    {
      name: isTemplateReadOnly(attachmentTemplate, isLoading)
        ? 'View Attachment'
        : 'Edit Attachment',
      action: ['ACTION', () => onEdit(attachmentTemplate)],
      ...(isTemplateReadOnly(attachmentTemplate, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(attachmentTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    },
    {
      name: 'Copy Attachment',
      action: ['ACTION', () => onCopy(attachmentTemplate.uuid)]
    },
    {
      name: filters?.displayActive ? 'Archive Attachment' : 'Activate Attachment',
      action: ['ACTION', () => onClickArchive(attachmentTemplate)],
      ...(isTemplateReadOnly(attachmentTemplate, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(attachmentTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterType.map(option => {
          if (filters?.filterTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Type',
        values: filters?.filterTypes,
        onChange: types => dispatch(updateAttachmentFilters({ filterTypes: types, pageNo: 0 }))
      },
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateAttachmentFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateAttachmentFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateAttachmentFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      },
      {
        options: optionsToFilterMarketingType.map(option => {
          if (filters?.filterMarketingTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Marketing Type',
        values: filters?.filterMarketingTypes,
        onChange: marketingTypes =>
          dispatch(updateAttachmentFilters({ filterMarketingTypes: marketingTypes, pageNo: 0 }))
      }
    ],
    autocompleteSearch: {
      options: optionsToFilterTags.map(option => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Tags',
      values: filters?.filterTags,
      onChange: tags => dispatch(updateAttachmentFilters({ filterTags: tags, pageNo: 0 }))
    },
    resetFilters: () => dispatch(resetAttachmentFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Attachment Templates',
    searchAction: (payload) => dispatch(updateAttachmentFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentAttachmentTemplate(), setCurrentAttachmentTemplate(row)],
      `/attachment-templates/details/${row.uuid}`
    );
  const onCopy = uuid =>
    onClickCopy(
      setCopyCurrentAttachmentTemplate,
      `/attachment-templates/details`,
      uuid,
      templates
    );

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedAttachmentTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={attachmentTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={templates}
          action={getPaginatedAttachmentTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateAttachmentFilters(payload))}
        />
      </div>
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  );
};

export default Template;
